import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Plus, Trash2, ChevronLeft, MapPin, CalendarClock, Camera,
  ChevronDown, ChevronUp, X, Building2, Eye, EyeOff,
} from "lucide-react";
import { prettify } from "./DeskDetail";

/* SiteInventory — dedicated page for OOH agencies (kind=site).
 *
 *  Each site row expands to show its bookings + inline add-booking form.
 *  Public flag toggle mirrors DeskDetail.
 */
export default function SiteInventory() {
  const [schema, setSchema] = useState(null);
  const [sites, setSites] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    const [{ data: cat }, { data: list }] = await Promise.all([
      api.get("/agency/desk-schemas"),
      api.get("/agency/desks/site"),
    ]);
    setSchema(cat.schemas.site);
    setSites(list.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!schema) return null;

  const editing = editingId ? sites.find((s) => s.id === editingId) : null;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8"
         data-testid="site-inventory-page">
      <Link to="/dashboard"
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-white/50 hover:text-white transition"
            data-testid="site-back">
        <ChevronLeft size={12} /> Back to Office
      </Link>
      <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">
            Agency Office · OOH
          </div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-1">
            Site Inventory
          </h1>
          <p className="mt-1.5 text-[12.5px] text-white/50">
            {sites.length} site{sites.length === 1 ? "" : "s"} · bookings + availability shown on your public profile.
          </p>
        </div>
        <button onClick={() => { setEditingId(null); setShowForm(true); }}
          className="h-9 px-4 rounded-full text-[10.5px] tracking-[0.18em] uppercase font-semibold bg-white text-black hover:bg-white/90 inline-flex items-center gap-1.5"
          data-testid="site-new-btn">
          <Plus size={12} /> New Site
        </button>
      </div>

      {sites.length === 0 ? (
        <div className="mt-8 cs-card rounded-2xl p-12 text-center" data-testid="site-empty">
          <div className="w-14 h-14 rounded-full bg-white/8 mx-auto flex items-center justify-center text-white/50">
            <Building2 size={22} />
          </div>
          <div className="mt-4 font-display text-lg uppercase tracking-tight">Empty inventory</div>
          <p className="mt-1.5 text-sm text-white/50 max-w-md mx-auto">
            Add your first OOH site to start listing. Once public, it becomes discoverable
            to media buying agencies and clients via your agency profile.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="site-list">
          {sites.map((s) => (
            <SiteRow key={s.id} site={s} schema={schema} open={openId === s.id}
                     onToggle={() => setOpenId(openId === s.id ? null : s.id)}
                     onEdit={() => { setEditingId(s.id); setShowForm(true); }}
                     onChanged={load} />
          ))}
        </div>
      )}

      {showForm && (
        <SiteFormModal schema={schema} site={editing}
                       onClose={() => { setShowForm(false); setEditingId(null); }}
                       onSaved={() => { setShowForm(false); setEditingId(null); load(); }} />
      )}
    </div>
  );
}

/* ---- row ---------------------------------------------------------------- */

function SiteRow({ site, schema, open, onToggle, onEdit, onChanged }) {
  const [bookings, setBookings] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadBookings = useCallback(async () => {
    const { data } = await api.get(`/agency/desks/site/${site.id}/bookings`);
    setBookings(data.bookings || []);
  }, [site.id]);
  useEffect(() => { if (open && !bookings) loadBookings(); }, [open, bookings, loadBookings]);

  const del = async () => {
    if (!window.confirm(`Delete "${site.title}"? Bookings will be removed too.`)) return;
    setBusy(true);
    try {
      await api.delete(`/agency/desks/site/${site.id}`);
      toast.success("Deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const togglePublic = async () => {
    try {
      await api.patch(`/agency/desks/site/${site.id}`, { is_public: !site.is_public });
      toast.success(site.is_public ? "Hidden from public profile" : "Now public");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const d = site.data || {};

  return (
    <div className="cs-card rounded-xl overflow-hidden" data-testid={`site-row-${site.id}`}>
      <div className="p-4 flex items-center gap-3 flex-wrap">
        {d.photo_url ? (
          <img src={d.photo_url} alt="" className="w-20 h-20 object-cover rounded-md flex-none border border-white/10"
               onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div className="w-20 h-20 rounded-md bg-white/6 flex-none flex items-center justify-center text-white/30">
            <Camera size={18} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-base uppercase tracking-tight truncate">{site.title}</div>
            <span className={"px-2 py-0.5 rounded-full text-[9.5px] tracking-[0.22em] uppercase font-semibold " +
              (site.status === "booked" ? "bg-amber-500/15 text-amber-300" :
               site.status === "available" ? "bg-emerald-500/15 text-emerald-300" :
               "bg-white/8 text-white/60")}>
              {prettify(site.status)}
            </span>
            <span className={"px-2 py-0.5 rounded-full text-[9px] tracking-[0.22em] uppercase font-semibold " +
              (site.is_public ? "bg-emerald-500/15 text-emerald-300" : "bg-white/8 text-white/50")}
              data-testid={`site-public-flag-${site.id}`}>
              {site.is_public ? "Public" : "Private"}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 text-[11.5px] text-white/60">
            <span><MapPin size={11} className="inline mr-0.5 text-white/40" /> {d.city}{d.locality ? `, ${d.locality}` : ""}</span>
            {d.medium_type && <span>· {prettify(d.medium_type)}</span>}
            {d.size && <span>· {d.size}</span>}
            {d.rate_month && <span>· ₹{Number(d.rate_month).toLocaleString("en-IN")}/mo</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={togglePublic}
            className="p-2 rounded-md hover:bg-white/8 text-white/50 hover:text-white"
            data-testid={`site-toggle-public-${site.id}`}>
            {site.is_public ? <Eye size={14} /> : <EyeOff size={14} />}
          </button>
          <button onClick={onToggle}
            className="p-2 rounded-md hover:bg-white/8 text-white/60 hover:text-white"
            data-testid={`site-expand-${site.id}`}>
            {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
          <button onClick={onEdit}
            className="p-2 rounded-md hover:bg-white/8 text-white/60 hover:text-white text-[10px] tracking-[0.2em] uppercase"
            data-testid={`site-edit-${site.id}`}>
            Edit
          </button>
          <button onClick={del} disabled={busy}
            className="p-2 rounded-md hover:bg-red-500/15 text-white/50 hover:text-red-300"
            data-testid={`site-delete-${site.id}`}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/8 bg-black/25 px-4 py-4"
             data-testid={`site-bookings-${site.id}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 inline-flex items-center gap-1.5">
              <CalendarClock size={12} /> Bookings
            </div>
          </div>
          {!bookings ? (
            <div className="mt-3 text-sm text-white/45">Loading…</div>
          ) : (
            <>
              {bookings.length === 0
                ? <div className="mt-3 text-[12px] text-white/50">No bookings yet.</div>
                : (
                  <ul className="mt-3 divide-y divide-white/6">
                    {bookings.map((b) => (
                      <li key={b.id} className="py-2 flex items-center justify-between gap-3"
                          data-testid={`site-booking-row-${b.id}`}>
                        <div>
                          <div className="text-[13px] text-white/85">{b.client_name}</div>
                          <div className="text-[11px] text-white/45 tabular-nums">
                            {b.from_date} → {b.to_date}
                            {b.amount ? ` · ₹${Number(b.amount).toLocaleString("en-IN")}` : ""}
                          </div>
                        </div>
                        <button onClick={async () => {
                          if (!window.confirm("Cancel booking?")) return;
                          await api.delete(`/agency/desks/site/${site.id}/bookings/${b.id}`);
                          toast.success("Cancelled");
                          loadBookings();
                          onChanged();
                        }}
                        className="text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-red-300"
                        data-testid={`site-booking-delete-${b.id}`}>
                          Cancel
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              <BookingForm siteId={site.id}
                onAdded={() => { loadBookings(); onChanged(); }} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BookingForm({ siteId, onAdded }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ client_name: "", from_date: today, to_date: today, amount: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.client_name.trim()) return;
    setBusy(true);
    try {
      await api.post(`/agency/desks/site/${siteId}/bookings`, {
        ...f,
        amount: f.amount === "" ? null : Number(f.amount),
      });
      toast.success("Booked");
      setF({ client_name: "", from_date: today, to_date: today, amount: "" });
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-4 grid sm:grid-cols-5 gap-2"
          data-testid={`site-booking-form-${siteId}`}>
      <input placeholder="Client name" value={f.client_name}
        onChange={(e) => setF({ ...f, client_name: e.target.value })}
        className="bg-black/40 border border-white/15 rounded-md h-9 px-3 text-sm sm:col-span-2"
        data-testid="booking-form-client" />
      <input type="date" value={f.from_date}
        onChange={(e) => setF({ ...f, from_date: e.target.value })}
        className="bg-black/40 border border-white/15 rounded-md h-9 px-2 text-[12px]"
        data-testid="booking-form-from" />
      <input type="date" value={f.to_date}
        onChange={(e) => setF({ ...f, to_date: e.target.value })}
        className="bg-black/40 border border-white/15 rounded-md h-9 px-2 text-[12px]"
        data-testid="booking-form-to" />
      <div className="flex gap-2">
        <input type="number" placeholder="Amount ₹" value={f.amount}
          onChange={(e) => setF({ ...f, amount: e.target.value })}
          className="bg-black/40 border border-white/15 rounded-md h-9 px-3 text-sm flex-1 min-w-0"
          data-testid="booking-form-amount" />
        <button type="submit" disabled={busy}
          className="h-9 px-3 rounded-md text-[10px] tracking-[0.18em] uppercase font-semibold bg-white text-black hover:bg-white/90 disabled:opacity-60"
          data-testid="booking-form-submit">
          Book
        </button>
      </div>
    </form>
  );
}

/* ---- form modal --------------------------------------------------------- */

function SiteFormModal({ schema, site, onClose, onSaved }) {
  const [data, setData] = useState(() => ({ ...(site?.data || {}) }));
  const [isPublic, setIsPublic] = useState(site ? !!site.is_public : true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    for (const f of schema.fields) {
      if (f.required && !data[f.key]) { toast.error(`Missing: ${f.label}`); return; }
    }
    setBusy(true);
    try {
      const body = { data, is_public: isPublic };
      if (site) await api.patch(`/agency/desks/site/${site.id}`, body);
      else      await api.post(`/agency/desks/site`, body);
      toast.success(site ? "Updated" : "Added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4"
         data-testid="site-form-modal">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative z-10 w-full max-w-2xl cs-card rounded-2xl p-6 my-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              {site ? "Edit" : "New"} · OOH Site
            </div>
            <div className="mt-0.5 font-display text-xl uppercase tracking-tight">Site details</div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-white/8">
            <X size={16} />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {schema.fields.map((f) => (
            <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
              <label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                {f.label} {f.required && <span className="text-red-400">*</span>}
              </label>
              <FieldInput f={f} value={data[f.key] ?? ""}
                          onChange={(v) => setData((s) => ({ ...s, [f.key]: v }))}
                          testid={`site-field-${f.key}`} />
            </div>
          ))}
        </div>
        <label className="mt-4 flex items-center gap-2 text-[12px] text-white/70"
               data-testid="site-form-public-wrap">
          <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)}
                 data-testid="site-form-public" />
          Show on public agency profile
        </label>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="h-10 px-4 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold border border-white/25 hover:bg-white/8">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            className="h-10 px-6 rounded-full text-[11px] tracking-[0.2em] uppercase font-semibold bg-white text-black hover:bg-white/90 disabled:opacity-60"
            data-testid="site-form-submit">
            {busy ? "Saving…" : (site ? "Update" : "Add Site")}
          </button>
        </div>
      </form>
    </div>
  );
}

function FieldInput({ f, value, onChange, testid }) {
  if (f.type === "textarea")
    return <textarea value={value} onChange={(e) => onChange(e.target.value)}
             className="mt-1 w-full h-20 bg-black/40 border border-white/15 rounded-md px-3 py-2 text-sm resize-none"
             data-testid={testid} />;
  if (f.type === "select")
    return <select value={value} onChange={(e) => onChange(e.target.value)}
             className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
             data-testid={testid}>
             <option value="">—</option>
             {(f.options || []).map((o) => <option key={o} value={o}>{prettify(o)}</option>)}
           </select>;
  const t = f.type === "number" ? "number" :
            f.type === "email"  ? "email"  :
            f.type === "url"    ? "url"    :
            f.type === "date"   ? "date"   : "text";
  return <input type={t} value={value} onChange={(e) => onChange(e.target.value)}
           className="mt-1 w-full bg-black/40 border border-white/15 rounded-md h-10 px-3 text-sm"
           data-testid={testid} />;
}
